import React, { useCallback, useEffect, useRef, useState } from "react";

// Internal Imports
import DataGrid from "../../common/components/DataGrid/DataGrid";
import MenuLayout from "../../common/components/MenuLayout/MenuLayout";
import ConfirmationModal from "../../common/components/Modal/CorfimationModal";
import Modal from "../../common/components/Modal/Modal";
import Paint from "../../common/components/Paint/Paint";
import { COLORS } from "../../common/constants/colors";
import { getError, openNotification, tryCatch } from "../../common/constants/Utilities";
import { Mode } from "../../common/types/AllTypes";
import ViewContainer from "../../common/components/ViewContainer/ViewContainer";
import { useListDepartmentByUer } from "../../hooks/useListDepartment";
import IColumn from "../../interfaces/IColumn";
import { IMenu } from "../../interfaces/IMenu";
import { IQuestionGroup } from "../../interfaces/IQuestion";
import { DeactivateQuestionsGroupByID, GetQuestionsGroupByID, GetQuestionsGroupByUserID } from "../../services/questionGroup";
import AddUpdateQuestionGroup from "./AddUpdateQuestionGroup";
import './GrupoPreguntas.css';
import { LoadingIcon } from "../../common/constants/cssElements";

// Interfaces
interface Props {
    menu: IMenu
}

const GrupoPreguntas: React.FC<Props> = ({ menu }) => {

    // States
    const { listDepartment, loadingDeparment } = useListDepartmentByUer();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [mode, setMode] = useState<Mode>('Add');
    const [listQuestionGroup, setListQuestionGroup] = useState<IQuestionGroup[]>([]);
    const [questionGroupUpdate, setQuestionGroupUpdate] = useState<IQuestionGroup>();
    const groupDep = useRef<IQuestionGroup>();
    const [searchV, setSearchV] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [idDetail, setIdDetail] = useState<number | null>(null);

    // Consts
    const columns: IColumn[] = [
        {
            title: 'Nombre',
            dataIndex: 'nombre',
        },
        {
            title: 'Departamento',
            dataIndex: 'departamentoNombre',
        },
        {
            title: <i className="fa-solid fa-rotate button_refresh" onClick={() => { handleGetQuestionGroupsByUserID(); setSearchV('') }}></i>,
            dataIndex: '',
            render: (questionG: IQuestionGroup) => loadingDetail && idDetail === questionG.grupoPreguntaID ? <LoadingIcon color={COLORS.blue_active} size={'20px'} /> : <></>
        }
    ];


    // handles
    const handleGetQuestionGroupsByUserID = () => {
        tryCatch(() => {
            if (!!listDepartment) {
                setLoading(true);
                GetQuestionsGroupByUserID()
                    .then((response => {

                        const listGroup: IQuestionGroup[] = response.data;
                        listGroup.forEach(qtGroup => {
                            qtGroup.departamentoNombre = listDepartment.find(dep => dep.departamentoGUID === qtGroup.departamentoGUID)?.nombre;
                        })
                        setListQuestionGroup(listGroup);


                    }))
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoading(false));
            }
        })
    }

    const openModalUpdate = () => {
        if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Grupo tiene el Departamento Deshabilitado`, 'Contáctese con el equipo Bis para más información.');
        } else {
            setMode('Update');
            setAddUpdateModal(true);
        }
    }

    const handleGetQuestionGroupByID = (Qtgroup: IQuestionGroup) => {
        setQuestionGroupUpdate(undefined);
        groupDep.current = undefined;

        if (!!Qtgroup) {
            setIdDetail(Qtgroup.grupoPreguntaID!);
            setLoadingDetail(true);
            tryCatch(() => {
                GetQuestionsGroupByID(Qtgroup.grupoPreguntaID!)
                    .then(({ data }) => {
                        setQuestionGroupUpdate(data);
                        groupDep.current = Qtgroup;
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoadingDetail(false))
            })
        }
    }


    const handleDeactiveQuestionGruop = () => {
        tryCatch(() => {
            if (!!questionGroupUpdate) {
                setLoadingAction(true);
                DeactivateQuestionsGroupByID(questionGroupUpdate.grupoPreguntaID!)
                    .then((response) => {
                        openNotification('success', `Grupo de Preguntas (${questionGroupUpdate.nombre}) eliminado`, response.msg!);
                        setListQuestionGroup(listQuestionGroup.filter(groupQ => groupQ.grupoPreguntaID !== questionGroupUpdate.grupoPreguntaID!));
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => {
                        setLoadingAction(false);
                        setDeleteModal(false);
                        setQuestionGroupUpdate(undefined);
                    })
            }
        })
    }

    // Methods
    const getQuestionsGroupFiltered = useCallback((): IQuestionGroup[] => {
        let questionsGrouFiltered: IQuestionGroup[] = [];
        const valueLowerCase = searchV.toLowerCase();

        if (!!valueLowerCase) {
            questionsGrouFiltered = listQuestionGroup.filter((Qtgroup) => Qtgroup.departamentoNombre?.toLowerCase().includes(valueLowerCase) || Qtgroup.nombre!.toLowerCase().includes(valueLowerCase));
        } else {
            questionsGrouFiltered = listQuestionGroup;
        }

        return questionsGrouFiltered;
    }, [searchV, listQuestionGroup]);

    //Effects
    useEffect(() => {
        handleGetQuestionGroupsByUserID()
    }, [, listDepartment]);

    return (
        <MenuLayout currentPage={menu.nombre}>
            <ViewContainer>
                <DataGrid
                    searchV={searchV}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    gridProps={{ rowKey: (record: any) => record.grupoPreguntaID }}
                    columns={columns}
                    searchMethod={setSearchV}
                    menu={menu}
                    loading={loading}
                    dataSource={getQuestionsGroupFiltered()}
                    setItemSelected={handleGetQuestionGroupByID}
                    addAction={{ active: true, method: () => { setMode('Add'); setAddUpdateModal(true) } }}
                    deleteAction={{ active: !!questionGroupUpdate, method: () => setDeleteModal(true) }}
                    updateAction={{ active: !!questionGroupUpdate, method: openModalUpdate }}
                />
            </ViewContainer>


            <ConfirmationModal
                title={menu.nombre}
                visible={deleteModal}
                setVisible={setDeleteModal}
                question="¿Seguro que deseas eliminar este Grupo de Preguntas?"
                method={handleDeactiveQuestionGruop}
                loading={loadingAction}
            />

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {mode === 'Add' ? 'Crear' : 'Modificar'}  <Paint color={COLORS.blue_main(1)}>Grupo de Preguntas</Paint></nav>} visible={addUpdateModal} setVisible={setAddUpdateModal} askBeforeClose={true}>
                <AddUpdateQuestionGroup setDataList={setListQuestionGroup} dataList={listQuestionGroup} questionGroupUpdate={questionGroupUpdate} mode={mode} setQuestionGroupUpdate={setQuestionGroupUpdate} setAddUpdateModal={setAddUpdateModal} listDepartment={listDepartment} loadingDeparment={loadingDeparment} />
            </Modal>
        </MenuLayout>
    );
}


export default GrupoPreguntas